/**
 * Produces context model for updating custom list selection display
 * @param {Object} loadedRoot Root of external data node tree cached on client
 * @param {Array} selectorFields Ordered list of selector fields
 * @param {Object} selectorValues Map of selector values keyed by selector field id
 * @param {String} selectDefaultName Name of default select option
 * @param {String} rowId Id of selected node if a full selection has been made
 * @param {Boolean} allowObsoleteSelection True if obsolete selections are allowed
 * @param {Function} loadChildrenCallback Async function awaited by this method when data needs to be lazily loaded
 * @returns An object containing the new context for custom list selection display
 */
export async function updateSelectorsStatic(
    loadedRoot,
    selectorFields,
    selectorValues,
    selectDefaultName,
    rowId,
    allowObsoleteSelection,
    loadChildrenCallback
) {
    let node = loadedRoot;
    let isPrevSelected = true;

    const resultObject = new UpdateSelectorsResult();

    // If a full selection has been made, there's a possibility it is now
    // obsolete and we will have to manually add it to the data model
    const isCompleteSelection = Boolean(rowId);

    for (let i = 0; i < selectorFields.length; i++) {
        const sf = selectorFields[i];

        // if no previous selected, the options for this selector should be empty
        if (!isPrevSelected) {
            resultObject.selectorOptions[sf.PublicId] = [];
            continue;
        }

        // Previous selector had value, so we need to populate the options
        // of this selector

        // If the children of the current node have not been loaded, we need to lazily load them.
        // The initial tree constructed during project model extension should populate enough of
        // the tree so this isn't invoked many times during element initialization.
        if (!node.CompletelyLoaded && loadChildrenCallback) {
            const parentSubroot = await loadChildrenCallback(selectorFields, selectorValues, i);
            if (!parentSubroot) {
                throw new Error("An attempt to lazily load the children of a node failed unexpectedly.");
            }
            node.SortedChildren = parentSubroot.SortedChildren;
            node.Children = parentSubroot.Children;
            node.CompletelyLoaded = true;
            resultObject.mutatedCustomListData = true;
        }

        // Keep track of this node's children. We will use these lists
        // to populate the options for this selector
        const children = node.Children;
        const sortedChildren = node.SortedChildren;

        // If this node has a val, advance current node to the selected child
        const val = selectorValues[sf.PublicId];
        if (val && val != -1) {
            let selectedChild = children[val];

            // If we're at a selector with an unrecognized value and this is the
            // initial load of the element, we may have an obsolete selection.
            // If it's a full selection, we should continue to allow it for the given
            // project if it doesn't change. If it's a partial selection, we will treat
            // any obsolete selector values as empty. This matches the server behavior.
            // This is safe because any truly invalid selections posing as obsolete
            // selections will still be blocked during save.
            if (allowObsoleteSelection && !selectedChild) {
                // If this is an obsolete value in a partial selection, just treat
                // it as if nothing was selected.
                if (!isCompleteSelection) {
                    isPrevSelected = false;
                    resultObject.fieldsToClear.push(sf);

                    resultObject.selectorOptions[sf.PublicId] = getSelectorOptionsFromChildrenList(
                        selectDefaultName,
                        sortedChildren,
                        sf.DisplayType
                    );

                    continue;
                }

                // This was a full, obsolete selection. Fabricate a data node
                // to represent the saved obsolete selection.
                selectedChild = new ExternalDataNode();
                selectedChild.IsObsolete = true;
                selectedChild.CompletelyLoaded = true; // Restored obsolete nodes should never be lazily loaded

                // If this is the last selector field and we're creating a node
                // to represent an obsolete selection, mark the leaf node to fill
                // with the data that's currently saved.
                if (i === selectorFields.length - 1) {
                    resultObject.selectedObsoleteNodeToPopulate = selectedChild;
                    selectedChild.RowId = rowId;
                }
                // Add this fabricated child to the dictionary of the node's children
                // so it shows up as an option in the selector.
                children[val] = selectedChild;
                sortedChildren.push(val);
                resultObject.mutatedCustomListData = true;
            }

            // Advance the node pointer to the child so we can continue populating
            // subsequent selector options
            node = selectedChild;
        } else {
            // Reached a selector node without a value. Indicate that all subsequent selectors
            // should not have options until a choice is made for this one. This will also stop
            // us from advancing the node pointer.
            isPrevSelected = false;
        }

        // Fill in selector options for this node
        resultObject.selectorOptions[sf.PublicId] = getSelectorOptionsFromChildrenList(
            selectDefaultName,
            sortedChildren,
            sf.DisplayType
        );
    }

    if (isPrevSelected && node.RowId) {
        // full selection populates row id and output values
        resultObject.rowId = node.RowId;
        resultObject.outputValues = node.LeafData;
    }

    return resultObject;
}

/**
 * Converts the sorted children list of a node into a list of options for a selector.
 * @param {string} selectDefaultName Name for default selection option.
 * @param {Array} sortedChildren Ordered children to create options from.
 * @param {DISPLAY_TYPE} displayType Display type of the selector field.
 * @returns A dictionary of options for a selector.
 */
function getSelectorOptionsFromChildrenList(selectDefaultName, sortedChildren, displayType) {
    const optionsDict = displayType === DISPLAY_TYPE.RadioButtons ? [] : [{ text: selectDefaultName, value: "" }];
    for (const key in sortedChildren) {
        optionsDict.push({ text: sortedChildren[key], value: sortedChildren[key] });
    }
    return optionsDict;
}

/**
 * Represents a node in the custom list data model. Analogous to ExternalDataNode in the server.
 */
export class ExternalDataNode {
    constructor() {
        this.Children = {};
        this.SortedChildren = [];
        this.LeafData = [];
        this.RowId = null;
        this.IsObsolete = null;
        this.CompletelyLoaded = false;
    }
}

/**
 * An object containing the results of updating selectors for a custom list element.
 * @property {object} selectorOptions           - A dictionary of selector options for each selector field.
 * @property {string} rowId                     - The row id of the selected node.
 * @property {Array} outputValues               - The output values of the selected node.
 * @property {boolean} mutatedCustomListData    - Whether the custom list data model was mutated.
 * @property {Array} fieldsToClear              - If populated, indicates caller should clear incomplete selected data for provided fields.
 * @property {ExternalDataNode} selectedObsoleteNodeToPopulate - If an obsolete node was selected, this is the node to populate.
 */
export class UpdateSelectorsResult {
    constructor() {
        (this.selectorOptions = {}),
            (this.rowId = null),
            (this.outputValues = []),
            (this.mutatedCustomListData = false),
            (this.fieldsToClear = []),
            (this.selectedObsoleteNodeToPopulate = null);
    }
}

/**
 * Options for display type of an output field. Analogous to DisplayType enum in the server.
 */
export const DISPLAY_TYPE = Object.freeze({
    Unknown: 0,
    TextBox: 1,
    Static: 2,
    None: 3,
    Dropdown: 4,
    RadioButtons: 5
});
